import { useEffect, useState } from "react";

// Module-scoped so every component instance sharing an endpoint reuses one
// in-flight/resolved fetch instead of each issuing its own request.
const cachePromises = new Map();

// Strips query/hash before the URL ever reaches an Error message, a
// console.error, or any logging/monitoring pipeline downstream of it — so
// that if a caller ever adds query params (e.g. a token in the URL, which
// this hook does not do today) they still can't end up in logs.
function sanitizeUrlForLogging(url) {
    try {
        const { origin, pathname } = new URL(url);
        return `${origin}${pathname}`;
    } catch {
        return "[unparseable url]";
    }
}

function fetchDrupalSettings(url, apiKey) {
    if (!cachePromises.has(url)) {
        const promise = fetch(url, {
            headers: {
                accept: "application/json",
                // SECURITY NOTE: this request runs in the visitor's browser, so
                // this header — and therefore the key itself — is visible to
                // anyone who opens devtools, regardless of how the env var
                // supplying it is named. This is only appropriate if the key is
                // not meant to be a real secret (e.g. it just identifies the
                // calling app rather than gating access to sensitive data). If it
                // needs to stay confidential, this request has to happen
                // server-side instead (e.g. back through a Netlify Edge Function
                // or Function), not directly from the browser.
                "api-key": apiKey,
            },
            // Don't leak this page's URL (or the config endpoint's URL, on any
            // redirect) to Drupal — or onward from Drupal to a third party — via
            // the Referer header.
            referrerPolicy: "no-referrer",
        })
            .then((res) => {
                if (!res.ok) {
                    // Sanitized: never let a URL that could carry auth material
                    // (query-string tokens, etc.) reach an Error message that
                    // might get logged or surfaced to an error-tracking service.
                    throw new Error(
                        `${sanitizeUrlForLogging(url)} responded ${res.status}`
                    );
                }
                return res.json();
            })
            .catch((error) => {
                cachePromises.delete(url); // let the next caller retry
                throw error;
            });
        cachePromises.set(url, promise);
    }
    return cachePromises.get(url);
}

/**
 * Fetches this site's banner copy directly from Drupal (cross-origin —
 * requires Drupal's CORS config to allow this site's origin AND the
 * `api-key` request header specifically, since a custom header triggers a
 * CORS preflight). Drupal returns a flat, text-only object, e.g.:
 *
 *   {
 *     "text": "Seems like you are in @current_country. Would you like to go to @visitor_country website?",
 *     "bt_stay": "Stay",
 *     "bt_leave": "Leave"
 *   }
 *
 * There's no country list and no URLs in it — just copy for whichever site
 * instance is serving this response. Matching the visitor's country to a
 * destination, and finding that destination's URL, both come from the
 * page's own hreflang tags instead (see useHreflangAlternates.js).
 *
 * `text` may contain the literal tokens "@current_country" and
 * "@visitor_country", both replaced by the caller with a country display
 * name (see GeoBanner.js's fillTokens).
 *
 * Credentials note: the API key is sent only via the `api-key` request
 * header, never appended to the URL — so it can't leak through browser
 * history, server access logs, or the Referer header. See the SECURITY
 * NOTE in fetchDrupalSettings above for the header's own limits.
 *
 * @param {string} configUrl absolute URL, e.g.
 *   "https://cms.example.com/api/drupal-config"
 * @param {string} apiKey sent as the `api-key` header
 */
export default function useDrupalSettings(configUrl, apiKey) {
    const [state, setState] = useState({ status: "loading" });

    useEffect(() => {
        let cancelled = false;

        if (!configUrl) {
            setState({
                status: "error",
                error: new Error("useDrupalSettings: configUrl is required"),
            });
            return undefined;
        }

        fetchDrupalSettings(configUrl, apiKey)
            .then((data) => {
                if (cancelled) return;
                setState({
                    status: "ready",
                    text: data?.ln_seo_ipredirect_settings?.text,
                    stayLabel: data?.ln_seo_ipredirect_settings?.bt_stay,
                    leaveLabel: data?.ln_seo_ipredirect_settings?.bt_leave,
                });
            })
            .catch((error) => {
                if (cancelled) return;
                setState({ status: "error", error });
            });

        return () => {
            cancelled = true;
        };
    }, [configUrl, apiKey]);

    return state;
}
